import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '@shared/hooks/useStore';
import { Home, ChevronRight, ArrowUpDown } from 'lucide-react';
import { ROUTES } from '@shared/configs/constants';
import { Publication } from '@shared/types/publication';
import PublicationCard from '@shared/components/common/PublicationCard';
import Pagination from '@shared/components/pagination/Pagination';
import { showQuickViewModal } from '@user/components/publication/QuickViewModal';
import TitleAccent from '../home/components/TitleAccent';
import defaultCover from '@shared/assets/images/default-cover.png';
import NotFoundPage from '@/shared/components/common/NotFoundPage';

const MagazineDetailPage = observer(() => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { modalStore, magazineStore, apiStore } = useStore();
    const { magazineDetail: magazine, magazinePublications: publications, totalMagazinePublications: totalRecords } = magazineStore;
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const isLoading = apiStore.isLoading;
    const pageSize = 12;

    const toggleSort = () => {
        setSortOrder(prev => (prev === 'DESC' ? 'ASC' : 'DESC'));
        setCurrentPage(1);
    };

    const handlePublicationClick = (publication: Publication) => {
        navigate(`/publications/${publication.id}`);
    };

    const handleQuickView = (publication: Publication) => {
        showQuickViewModal(publication, modalStore);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        const element = document.getElementById('publications-list');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (!id) return;
        magazineStore.fetchMagazineDetail(id);
    }, [id, magazineStore]);

    useEffect(() => {
        if (!id) return;
        magazineStore.fetchMagazinePublications(id, {
            page: currentPage,
            size: pageSize,
            sortKey: 'release_date',
            sortDir: sortOrder
        });
    }, [id, currentPage, magazineStore, pageSize, sortOrder]);

    useEffect(() => {
        return () => {
            magazineStore.clean();
        };
    }, [magazineStore]);

    if (isLoading && !magazine) {
        return (<></>);
    }

    if (!magazine) {
        return (
            <NotFoundPage />
        );
    }

    const getFrequencyLabel = (freq: string) => {
        switch (freq) {
            case 'DAILY': return '日刊';
            case 'WEEKLY': return '週刊';
            case 'BIWEEKLY': return '隔週刊';
            case 'MONTHLY': return '月刊';
            case 'BIMONTHLY': return '隔月刊';
            case 'QUARTERLY': return '季刊';
            case 'SEMIANNUAL': return '半年刊';
            case 'YEARLY': return '年刊';
            default: return '不定期';
        }
    };

    return (
        <div className='container mx-auto px-4 py-6 md:py-12 space-y-6 md:space-y-12 text-break'>
            {/* Breadcrumb */}
            <nav className='hidden md:flex items-center gap-2 text-[11px] font-medium text-[#404040]'>
                <button
                    onClick={() => navigate('/')}
                    className='flex items-center gap-1 hover:text-primary transition-colors flex-shrink-0'
                >
                    <Home size={14} />
                    ホーム
                </button>
                <ChevronRight size={14} className='text-[#404040]' />
                <button
                    onClick={() => navigate(ROUTES.magazines.href)}
                    className='hover:text-primary transition-colors flex-shrink-0'
                >
                    雑誌
                </button>
                <ChevronRight size={14} className='text-[#404040]' />
                <span className='line-clamp-1'>
                    {magazine.title.length > 50 ? magazine.title.slice(0, 50) + '...' : magazine.title}
                </span>
            </nav>

            <div>
                {/* Magazine Header */}
                <div className='flex flex-col lg:flex-row gap-6 md:gap-8'>
                    {/* Left: Magazine Cover */}
                    <div className='mx-auto flex-shrink-0 w-[327px] xl:w-[423px]'>
                        <img
                            src={magazine.linkImg}
                            alt={magazine.title}
                            className='w-full aspect-[3/4] object-fill border border-white shadow-sm'
                            onError={(e) => {
                                e.currentTarget.src = defaultCover;
                            }}
                        />
                    </div>

                    {/* Right: Magazine Info */}
                    <div className='flex-1'>
                        <h1 className='text-[29px] font-semibold leading-[36px] mb-3'>
                            {magazine.title}
                        </h1>

                        {/* Metadata Grid */}
                        <div className='flex gap-[52px]'>
                            <div className='flex flex-col gap-2 items-center'>
                                <span className='text-[#999999] font-normal leading-[29px]'>発行頻度</span>
                                <span className='font-normal leading-[29px]'>
                                    {getFrequencyLabel(magazine.publishFrequency)}
                                </span>
                            </div>
                            <div className='flex flex-col gap-2 items-center'>
                                <span className='text-[#999999] font-normal leading-[29px]'>巻数</span>
                                <span className='font-normal leading-[29px]'>
                                    {magazine.totalMagazineSeries}
                                </span>
                            </div>
                            <div className='flex flex-col gap-2 items-center'>
                                <span className='text-[#999999] font-normal leading-[29px]'>号数</span>
                                <span className='font-normal leading-[29px]'>
                                    {magazine.totalMagazineIssue}
                                </span>
                            </div>
                        </div>

                        <div className='h-[1px] w-full bg-[#D9D9D9] my-6' />

                        {/* Description */}
                        <div className='prose prose-sm max-w-none'>
                            <p className='leading-[29px] whitespace-pre-line'>
                                {magazine.description}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Back Issues Section */}
                <div className='md:mt-[60px]'>
                    <div className='flex items-center justify-between my-6 md:my-[30px]' id='publications-list'>
                        <div className='flex items-center gap-1'>
                            <TitleAccent />
                            <h2 className='font-semibold text-[20px] leading-[24px]'>
                                既刊号一覧 (
                                {totalRecords}
                                )
                            </h2>
                        </div>
                        <div className='flex-1 mx-[10px] h-[1px] bg-border' />
                        <button
                            onClick={toggleSort}
                            className='flex items-center gap-2 px-[15px] py-[10px] bg-[#F4F4F4] border border-[#E7E7E7] text-[#666666] leading-[21px]'
                        >
                            <span>{sortOrder === 'DESC' ? '降順' : '昇順'}</span>
                            <ArrowUpDown size={16} />
                        </button>
                    </div>
                    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-[24px] lg:gap-[36px] xl:gap-[48px] ${isLoading ? 'opacity-50' : ''}`}>
                        {publications.map(pub => (
                            <PublicationCard
                                key={pub.id}
                                publication={pub}
                                onClick={handlePublicationClick}
                                onQuickView={handleQuickView}
                            />
                        ))}
                    </div>

                    {publications.length === 0 && !isLoading && (
                        <div className='text-center py-12 text-gray-500'>
                            まだ既刊号がありません。
                        </div>
                    )}

                    <div className='mt-12'>
                        <Pagination
                            type='full-pagination'
                            currentPage={currentPage}
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default MagazineDetailPage;
